import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tabs,
  Tab,
  Rating,
  Chip,
  useTheme,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Fade,
  Paper,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import StarIcon from '@mui/icons-material/Star';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SortIcon from '@mui/icons-material/Sort';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import PersonIcon from '@mui/icons-material/Person';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { getImagePath } from '../utils/imageUtils';

const testimonials = {
  'Mathematics': [
    {
      name: 'Aarohi Narayan',
      class: 'Class 3',
      achievement: 'Gold Medal in IMO 2023',
      rating: 5,
      image: getImagePath('student-1.jpg'),
      quote: 'The structured approach to problem-solving and regular practice sessions helped me develop a strong foundation. The mock tests were particularly helpful in preparing for the actual competition.',
      tags: ['IMO', 'Gold Medal', 'Problem Solving'],
    },
  ],
  'Science': [
    {
      name: 'Aarohi Narayan',
      class: 'Class 4',
      achievement: 'Gold Medal in NSO 2024',
      rating: 5,
      image: getImagePath('student-2.jpg'),
      quote: 'The practical approach to learning science concepts and the emphasis on experimental skills helped me excel in the NSO. The video lectures were particularly insightful.',
      tags: ['NSO', 'Gold Medal', 'Science'],
    }
  ],
  'English': [],
  'Social Studies': [],
  'Hindi': [],
};

const Testimonials = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [filters, setFilters] = useState({
    medal: 'all',
    class: 'all',
    year: 'all',
  });
  const [sortBy, setSortBy] = useState(() => {
    const savedSort = localStorage.getItem('testimonialSortPreference');
    return savedSort || 'year-desc';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [savedSorts, setSavedSorts] = useState(() => {
    const saved = localStorage.getItem('savedTestimonialSorts');
    return saved ? JSON.parse(saved) : [];
  });
  const theme = useTheme();

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleFilterChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.value,
    });
  };

  const handleClearFilter = (filterName) => {
    setFilters({
      ...filters,
      [filterName]: 'all',
    });
  };

  const handleClearAllFilters = () => {
    setFilters({
      medal: 'all',
      class: 'all',
      year: 'all',
    });
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleSaveSort = () => {
    const newSort = {
      id: Date.now(),
      name: `Sort ${savedSorts.length + 1}`,
      value: sortBy,
      filters: { ...filters },
    };
    const updatedSorts = [...savedSorts, newSort];
    setSavedSorts(updatedSorts);
    localStorage.setItem('savedTestimonialSorts', JSON.stringify(updatedSorts));
  };

  const handleApplySavedSort = (savedSort) => {
    setSortBy(savedSort.value);
    setFilters(savedSort.filters);
  };

  const handleDeleteSavedSort = (id) => {
    const updatedSorts = savedSorts.filter(sort => sort.id !== id);
    setSavedSorts(updatedSorts);
    localStorage.setItem('savedTestimonialSorts', JSON.stringify(updatedSorts));
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== 'all');

  const getTabLabel = (index) => {
    const labels = ['Mathematics', 'Science', 'English', 'Social Studies', 'Hindi'];
    return labels[index];
  };

  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters, sortBy, selectedTab]);

  useEffect(() => {
    localStorage.setItem('testimonialSortPreference', sortBy);
  }, [sortBy]);

  const getFilteredTestimonials = () => {
    let filtered = testimonials[getTabLabel(selectedTab)];

    if (filters.medal !== 'all') {
      filtered = filtered.filter(t => t.achievement.includes(filters.medal));
    }

    if (filters.class !== 'all') {
      filtered = filtered.filter(t => t.class === filters.class);
    }

    if (filters.year !== 'all') {
      filtered = filtered.filter(t => t.achievement.includes(filters.year));
    }

    // Sort the filtered results
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'year-desc':
          return b.achievement.match(/\d{4}/)[0] - a.achievement.match(/\d{4}/)[0];
        case 'year-asc':
          return a.achievement.match(/\d{4}/)[0] - b.achievement.match(/\d{4}/)[0];
        case 'class-desc':
          return parseInt(b.class.split(' ')[1]) - parseInt(a.class.split(' ')[1]);
        case 'class-asc':
          return parseInt(a.class.split(' ')[1]) - parseInt(b.class.split(' ')[1]);
        case 'rating-desc':
          return b.rating - a.rating;
        case 'rating-asc':
          return a.rating - b.rating;
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'name-desc':
          return b.name.localeCompare(a.name);
        case 'achievement-desc':
          return b.achievement.localeCompare(a.achievement);
        case 'achievement-asc':
          return a.achievement.localeCompare(b.achievement);
        case 'quote-length-desc':
          return b.quote.length - a.quote.length;
        case 'quote-length-asc':
          return a.quote.length - b.quote.length;
        case 'tags-count-desc':
          return b.tags.length - a.tags.length;
        case 'tags-count-asc':
          return a.tags.length - b.tags.length;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredTestimonials = getFilteredTestimonials();

  const getUniqueValues = (key) => {
    const values = new Set();
    Object.values(testimonials).forEach(category => {
      category.forEach(testimonial => {
        if (key === 'medal') {
          if (testimonial.achievement.includes('Gold')) values.add('Gold');
          if (testimonial.achievement.includes('Silver')) values.add('Silver');
          if (testimonial.achievement.includes('Bronze')) values.add('Bronze');
        } else if (key === 'class') {
          values.add(testimonial.class);
        } else if (key === 'year') {
          const year = testimonial.achievement.match(/\d{4}/);
          if (year) values.add(year[0]);
        }
      });
    });
    return Array.from(values).sort();
  };

  const LoadingSkeleton = () => (
    <Grid container spacing={4}>
      {[1, 2, 3].map((item) => (
        <Grid item xs={12} md={6} key={item}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="circular" width={60} height={60} sx={{ mr: 2 }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={20} />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="text" width="80%" height={24} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Skeleton variant="text" width="40%" height={24} />
              </Box>
              <Skeleton variant="text" width="100%" height={80} />
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                {[1, 2, 3].map((tag) => (
                  <Skeleton key={tag} variant="rectangular" width={80} height={24} />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Success Stories
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" align="center" paragraph>
        Hear from our students who have achieved excellence in various Olympiad competitions
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          centered
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          {['Mathematics', 'Science', 'English','Social Studies','Hindi'].map((label, index) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <FilterListIcon color="primary" />
            <Typography variant="subtitle1">Filter by:</Typography>
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <Typography variant="body2" color="text.secondary">
              {filteredTestimonials.length} results found
            </Typography>
            {hasActiveFilters && (
              <Button
                startIcon={<ClearAllIcon />}
                onClick={handleClearAllFilters}
                variant="outlined"
                size="small"
              >
                Clear All Filters
              </Button>
            )}
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Medal</InputLabel>
            <Select
              name="medal"
              value={filters.medal}
              onChange={handleFilterChange}
              label="Medal"
            >
              <MenuItem value="all">All Medals</MenuItem>
              {getUniqueValues('medal').map(medal => (
                <MenuItem key={medal} value={medal}>{medal} Medal</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Class</InputLabel>
            <Select
              name="class"
              value={filters.class}
              onChange={handleFilterChange}
              label="Class"
            >
              <MenuItem value="all">All Classes</MenuItem>
              {getUniqueValues('class').map(cls => (
                <MenuItem key={cls} value={cls}>{cls}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Year</InputLabel>
            <Select
              name="year"
              value={filters.year}
              onChange={handleFilterChange}
              label="Year"
            >
              <MenuItem value="all">All Years</MenuItem>
              {getUniqueValues('year').map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Sort by</InputLabel>
            <Select
              value={sortBy}
              onChange={handleSortChange}
              label="Sort by"
              startAdornment={<SortIcon sx={{ mr: 1, color: 'primary.main' }} />}
            >
              <MenuItem value="year-desc">Year (Newest First)</MenuItem>
              <MenuItem value="year-asc">Year (Oldest First)</MenuItem>
              <MenuItem value="class-desc">Class (Higher First)</MenuItem>
              <MenuItem value="class-asc">Class (Lower First)</MenuItem>
              <MenuItem value="rating-desc">Rating (Highest First)</MenuItem>
              <MenuItem value="rating-asc">Rating (Lowest First)</MenuItem>
              <MenuItem value="name-asc">Name (A-Z)</MenuItem>
              <MenuItem value="name-desc">Name (Z-A)</MenuItem>
              <MenuItem value="achievement-desc">Achievement (Highest First)</MenuItem>
              <MenuItem value="achievement-asc">Achievement (Lowest First)</MenuItem>
              <MenuItem value="quote-length-desc">Quote Length (Longest First)</MenuItem>
              <MenuItem value="quote-length-asc">Quote Length (Shortest First)</MenuItem>
              <MenuItem value="tags-count-desc">Number of Tags (Most First)</MenuItem>
              <MenuItem value="tags-count-asc">Number of Tags (Least First)</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Save current sort and filters">
            <IconButton
              color="primary"
              onClick={handleSaveSort}
              sx={{ alignSelf: 'flex-end' }}
            >
              <BookmarkBorderIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {savedSorts.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Saved Sorts:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {savedSorts.map((savedSort) => (
                <Chip
                  key={savedSort.id}
                  label={savedSort.name}
                  onClick={() => handleApplySavedSort(savedSort)}
                  onDelete={() => handleDeleteSavedSort(savedSort.id)}
                  deleteIcon={<ClearIcon />}
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Box>
        )}

        {hasActiveFilters && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Active Filters:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {Object.entries(filters).map(([key, value]) => (
                value !== 'all' && (
                  <Chip
                    key={key}
                    label={`${key.charAt(0).toUpperCase() + key.slice(1)}: ${value}`}
                    onDelete={() => handleClearFilter(key)}
                    deleteIcon={<ClearIcon />}
                    color="primary"
                    variant="outlined"
                  />
                )
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      <Grid container spacing={4}>
        <Fade in={true} timeout={500}>
          <Grid item xs={12}>
            {isLoading ? (
              <LoadingSkeleton />
            ) : filteredTestimonials.length === 0 ? (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <SearchOffIcon
                  sx={{
                    fontSize: 48,
                    color: 'text.secondary',
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Results Found
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Try adjusting your filters or sorting options to find what you're looking for.
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleClearAllFilters}
                  startIcon={<ClearAllIcon />}
                >
                  Clear All Filters
                </Button>
              </Paper>
            ) : (
              <Grid container spacing={4}>
                {filteredTestimonials.map((testimonial, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Fade in={true} timeout={500} style={{ transitionDelay: `${index * 100}ms` }}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: theme.shadows[4],
                          },
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar
                              src={testimonial.image}
                              alt={testimonial.name}
                              sx={{
                                width: 60,
                                height: 60,
                                mr: 2,
                                transition: 'transform 0.2s',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                },
                              }}
                            />
                            <Box>
                              <Typography variant="h6" component="h3">
                                {testimonial.name}
                              </Typography>
                              <Typography variant="subtitle2" color="text.secondary">
                                {testimonial.class}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <EmojiEventsIcon sx={{ color: 'primary.main', mr: 1 }} />
                            <Typography variant="subtitle1" color="primary">
                              {testimonial.achievement}
                            </Typography>
                          </Box>

                          <Rating
                            value={testimonial.rating}
                            readOnly
                            precision={0.5}
                            emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                            sx={{ mb: 2 }}
                          />

                          <Typography variant="body1" paragraph>
                            "{testimonial.quote}"
                          </Typography>

                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {testimonial.tags.map((tag, idx) => (
                              <Chip
                                key={idx}
                                label={tag}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </CardContent>
                      </Card>
                    </Fade>
                  </Grid>
                ))}
              </Grid>
            )}
          </Grid>
        </Fade>
      </Grid>

      <Box sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Ready to Start Your Olympiad Journey?
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Join our program and become the next success story
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<SchoolIcon />}
          href="/demo-class"
        >
          Book a Free Demo Class
        </Button>
      </Box>
    </Container>
  );
};

export default Testimonials; 